import { useEffect } from "react";
import { IFormSection, TSubmitType } from "../../types/common";
import { API_PAYROLL, API_USERS } from "../../utils/apis";

import { URL_PAYROLL } from "../../utils/urls";
import useGet from "../common/useGet";
import { getResultOptions, stringifyName } from "../../utils/helpers";
import { TUser } from "../../types/context";
import {
  ALLOWANCE,
  BASE_SALARY,
  BONUS,
  BOOLEAN_SELECTIONS,
  MODAL,
} from "../../utils/constants";
import { Add } from "@mui/icons-material";

export type TFormCustomProps = {
  id?: number;
  submitType?: TSubmitType;
};

export const usePayrollForm = ({ form }) => {
  const sections: IFormSection[] = [
    {
      title: "Payroll Information",
      fields: [
        {
          field_name: "staff",
          type: "select",
          label: "Staff",
          props: { required: true },
          options_name: "staff",
        },
        {
          field_name: "bank_account_name",
          type: "text",
          label: "Bank Account Name",
          props: { required: true },
        },
        {
          field_name: "bank_account_number",
          type: "text",
          label: "Bank Account Number",
          props: { required: true },
        },
      ],
    },
    {
      title: "Salary Component",
      fields: [
        {
          field_name: "salary_components",
          type: MODAL,
          label: "Add Salary Component",
          props: { required: true, icon: <Add fontSize="small" />, wide: true },
          fields: [
            {
              field_name: "salary_component",
              type: "select",
              label: "Salary Components",
              props: { required: true },
              options_data: [
                { id: BASE_SALARY, name: "Base Salary" },
                { id: ALLOWANCE, name: "Allowance" },
                { id: BONUS, name: "Bonus" },
              ],
            },
            // BONUSES FIELDS
            {
              field_name: "bonus_amount",
              type: "number",
              label: "Bonus Amount",
              props: { required: true },
              visible: form?.data?.salary_component == BONUS,
            },
            // ALLOWANCE FIELDS
            {
              field_name: "allowance_amount",
              type: "number",
              label: "Allowance Amount",
              props: { required: true },
              visible: form?.data?.salary_component == ALLOWANCE,
            },
            {
              field_name: "allowance_type",
              type: "select",
              label: "Allowance Type",
              props: { required: true },
              visible: form?.data?.salary_component == ALLOWANCE,
              options_name: "allowance_types",
            },
            // BASE SALARY FIELDS
            {
              field_name: "gross_pay",
              type: "number",
              label: "Gross Pay",
              props: { required: true },
              visible: form?.data?.salary_component == BASE_SALARY,
            },
            {
              field_name: "has_nssf",
              type: "select",
              label: "Has NSSF",
              props: { required: true },
              options_data: BOOLEAN_SELECTIONS,
              visible: form?.data?.salary_component == BASE_SALARY,
            },
            {
              field_name: "has_paye",
              type: "select",
              label: "Has PAYE",
              props: { required: true },
              options_data: BOOLEAN_SELECTIONS,
              visible: form?.data?.salary_component == BASE_SALARY,
            },
            {
              field_name: "paye_amount",
              type: "number",
              label: "PAYE Amount",
              props: { required: true },
              visible: form?.data?.has_paye == 1,
            },
            // /salary_advancement
            {
              field_name: "salary_advancement",
              type: "select",
              label: "Salary Advancement",
              props: { required: true },
              options_data: BOOLEAN_SELECTIONS,
              visible: form?.data?.salary_component == BASE_SALARY,
            },
            {
              field_name: "advance_payment",
              type: "number",
              label: "Advance Payment",
              props: { required: true },
              visible: form?.data?.salary_advancement == 1,
            },
          ],
        },
      ],
    },
  ];

  const redirect = `${URL_PAYROLL}`;

  const getConfigOptions = () => {
    const { loadData: getStaff, data: staff } = useGet();
    const { loadData: getAllowanceTypes, data: allowanceTypes } = useGet();

    const getStaffOptions = (data: any) => {
      return data?.results?.length
        ? data.results.map((item: TUser) => ({
            id: item.id,
            name: stringifyName(item),
            data: item,
          }))
        : [];
    };
    useEffect(() => {
      getAllowanceTypes({
        api: "configurables/allowance-types/",
        params: {},
      });
      getStaff({
        api: API_USERS,
        params: {
          is_staff: true,
        },
      });
    }, []);
    return {
      staff: getStaffOptions(staff),
      allowance_types: getResultOptions(
        allowanceTypes,
        "type_of_allowance",
        "id",
      ),
    };
  };
  const options = {
    ...getConfigOptions(),
  };

  return { sections, redirect, options };
};

export function submitPayroll(payload: any, id: number | null) {
  //  on update
  if (id) {
    return {
      id,
      api: API_PAYROLL,
      params: payload,
    };
  }
  // on create
  return {
    api: API_PAYROLL,
    params: payload,
  };
}
